import type { ReactNode } from 'react';
import type { RequestPayload } from '@geoic/shared';
import { dateOnly, price as formatPrice } from '@/lib/format.js';

/**
 * Ariza tarkibi — 7 turning HAR BIRI uchun o'z maydonlari (B8 DoD).
 *
 * `payload` discriminated union, shuning uchun `kind` bo'yicha tarmoqlash
 * to'liq: yangi tur qo'shilsa `tsc` bu yerni ko'rsatadi va u jimgina
 * bo'sh kartochka bo'lib qolmaydi.
 */
export function RequestPayloadView({ payload }: { payload: RequestPayload | null }) {
  if (payload === null) {
    return <p className="text-sm text-n-600">Qo‘shimcha ma’lumot yo‘q.</p>;
  }

  switch (payload.kind) {
    case 'product':
      return (
        <Rows>
          <Row label="Mahsulot" value={payload.productTitle} />
          <Row label="Komplektatsiya" value={payload.variantTitle} />
          <Row label="Narx" value={formatPrice(payload.price)} />
          <Row label="Soni" value={String(payload.quantity)} />
        </Rows>
      );

    case 'cart':
      return (
        <div className="space-y-3">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-n-600 uppercase">
              <tr>
                <th className="py-1">Mahsulot</th>
                <th className="py-1 text-right">Soni</th>
                <th className="py-1 text-right">Narx</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-n-100">
              {payload.items.map((item, index) => (
                <tr key={`${item.productId}-${item.variantId}-${index}`}>
                  <td className="py-1.5 text-n-800">
                    {item.productTitle}
                    <span className="text-n-400"> · {item.variantTitle}</span>
                  </td>
                  <td className="py-1.5 text-right text-n-600">{item.quantity}</td>
                  <td className="py-1.5 text-right whitespace-nowrap text-n-600">
                    {formatPrice(item.price)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <Rows>
            <Row
              label="Jami"
              value={
                payload.totalAmount === null
                  ? 'Narxsiz pozitsiyalar bor'
                  : formatPrice(payload.totalAmount)
              }
            />
            <Row label="Yetkazish hududi" value={payload.deliveryRegion} />
          </Rows>
        </div>
      );

    case 'drone_show':
      return (
        <Rows>
          <Row label="Tadbir turi" value={payload.eventType} />
          <Row
            label="Sana"
            value={payload.eventDate ? dateOnly(String(payload.eventDate)) : null}
          />
          <Row label="Shahar" value={payload.eventCity} />
          <Row label="Joy" value={payload.venue} />
          <Row label="Tomoshabinlar" value={payload.audienceSize} />
          <Row label="Dronlar soni" value={payload.droneCount} />
          <Row label="Piroteхnika" value={payload.needPyro ? 'Kerak' : 'Kerak emas'} />
          <Row
            label="Davomiyligi"
            value={payload.durationMinutes && `${payload.durationMinutes} daq`}
          />
          <Row label="Byudjet" value={payload.budgetRange} />
          <Row label="Namuna fayllar" value={`${payload.referenceFiles.length} ta`} />
        </Rows>
      );

    case 'repair':
      return (
        <Rows>
          <Row label="Dron modeli" value={payload.droneModel} />
          <Row label="Seriya raqami" value={payload.serialNumber} />
          <Row
            label="Kafolat"
            value={
              payload.underWarranty === null
                ? 'Noma’lum'
                : payload.underWarranty
                  ? 'Kafolat ostida'
                  : 'Kafolatsiz'
            }
          />
          <Row label="Foto" value={`${payload.photos.length} ta`} />
          <Row label="Muammo" value={payload.problemDescription} multiline />
        </Rows>
      );

    case 'course':
      return (
        <Rows>
          <Row label="Kurs" value={payload.courseTitle} />
          <Row label="Tajriba" value={EXPERIENCE_LABELS[payload.experienceLevel]} />
          <Row label="Ishtirokchilar" value={payload.participantsCount} />
          <Row
            label="Boshlanish sanasi"
            value={payload.preferredStartDate ? dateOnly(String(payload.preferredStartDate)) : null}
          />
        </Rows>
      );

    case 'service':
      return (
        <Rows>
          <Row label="Xizmat" value={payload.serviceTitle} />
          <Row label="Obyekt turi" value={payload.objectType} />
          <Row label="Maydon" value={payload.areaHectares && `${payload.areaHectares} ga`} />
          <Row label="Hudud" value={payload.region} />
          <Row
            label="Muddat"
            value={payload.deadline ? dateOnly(String(payload.deadline)) : null}
          />
          <Row label="Fayllar" value={`${payload.files.length} ta`} />
        </Rows>
      );

    case 'general':
      return (
        <Rows>
          <Row label="Mavzu" value={payload.subject} />
        </Rows>
      );
  }
}

const EXPERIENCE_LABELS: Record<string, string> = {
  none: 'Tajribasiz',
  beginner: 'Boshlang‘ich',
  experienced: 'Tajribali',
};

function Rows({ children }: { children: ReactNode }) {
  return <dl className="grid gap-x-4 gap-y-2 sm:grid-cols-[minmax(0,180px)_1fr]">{children}</dl>;
}

/**
 * Bo'sh qiymatlar KO'RSATILMAYDI.
 *
 * Ariza formasida ixtiyoriy maydonlar ko'p; ularning hammasini «—» bilan
 * chizish kartochkani ikki barobar uzaytirib, to'ldirilgan maydonlarni
 * ko'rinmas qilardi.
 */
function Row({
  label,
  value,
  multiline = false,
}: {
  label: string;
  value: string | number | null | undefined | false;
  multiline?: boolean;
}) {
  if (value === null || value === undefined || value === '' || value === false) return null;

  return (
    <>
      <dt className="text-sm text-n-600">{label}</dt>
      <dd className={multiline ? 'text-sm whitespace-pre-wrap text-n-800' : 'text-sm text-n-800'}>
        {value}
      </dd>
    </>
  );
}
